'use client'

import { useState, useEffect, useRef, Suspense } from 'react'
import { signIn } from 'next-auth/react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import AllsioMark from '@/app/components/AllsioMark'
import { use2FALogin } from '@/app/components/use2FALogin'

interface Branding {
  platformName: string
  logoUrl: string | null
}

function LoginPageInner() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const setupDone = searchParams.get('setup') === 'done'
  const setupSkipped = searchParams.get('setup') === 'skipped'
  const {
    step, email, setEmail, password, setPassword, code, setCode,
    error, setError, loading, submitCredentials, submitCode, back, requestEmailFallback, useRecoveryCode, resendCooldown,
  } = use2FALogin({ onAuthenticated: () => { window.location.href = '/dashboard/launcher' } })

  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [passkeyAvailable, setPasskeyAvailable] = useState(false)
  const [branding, setBranding] = useState<Branding>({ platformName: 'Allsio', logoUrl: null })

  const totpInputRef = useRef<HTMLInputElement>(null)
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    fetch('/api/settings/branding')
      .then(r => r.json())
      .then(data => setBranding(data))
      .catch(() => {})
    if (typeof window !== 'undefined' && window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => setPasskeyAvailable(available))
        .catch(() => setPasskeyAvailable(false))
    }
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
    }
  }, [])

  // Delay TOTP focus by 1s to prevent password manager from injecting into the field
  useEffect(() => {
    if (step === 'totp') {
      focusTimerRef.current = setTimeout(() => {
        totpInputRef.current?.focus()
      }, 1000)
      return () => {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
      }
    }
  }, [step])

  async function handlePasskeySignIn() {
    setPasskeyLoading(true)
    setError('')
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser')

      const beginRes = await fetch('/api/auth/passkey/authenticate/begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const options = await beginRes.json()
      const { _challengeKey, ...optionsJSON } = options

      const authResponse = await startAuthentication(optionsJSON, false)

      const completeRes = await fetch('/api/auth/passkey/authenticate/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...authResponse, _challengeKey }),
      })
      const result = await completeRes.json()

      if (!result.success) {
        setError(result.error || 'Passkey authentication failed')
        return
      }

      const signInResult = await signIn('credentials', {
        email: result.email,
        password: '',
        passkeyToken: result.passkeyToken,
        redirect: false,
      })

      if (signInResult?.ok) {
        router.push('/dashboard')
      } else {
        setError('Sign in failed')
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled')
      } else if (err.name === 'NotSupportedError') {
        setPasskeyAvailable(false)
      } else {
        setError(err.message || 'Passkey authentication failed')
      }
    } finally {
      setPasskeyLoading(false)
    }
  }

  const inputStyle = {
    background: 'var(--brand-bg-elevated)',
    border: '1px solid var(--brand-border)',
    color: 'var(--brand-text-primary)',
    borderRadius: 'var(--brand-radius)',
  }

  const cardStyle = {
    background: 'var(--brand-bg-surface)',
    border: '1px solid var(--brand-border)',
    borderRadius: 'var(--brand-radius)',
  }

  const buttonStyle = {
    background: 'var(--brand-accent)',
    color: '#0d0d0d',
    borderRadius: 'var(--brand-radius)',
    fontFamily: 'var(--font-syncopate, sans-serif)',
  }

  return (
    <div
      className="flex-1 flex items-center justify-center px-4"
      style={{ background: 'var(--brand-bg-primary)' }}
    >
      <div className="w-full max-w-md">
        <div className="p-8 shadow-brand-strong" style={cardStyle}>

          {/* 2FA setup complete notice */}
          {setupDone && (
            <div className="mb-6 px-4 py-3 rounded-lg bg-green-900/40 border border-green-700 text-green-300 text-sm text-center">
              Two-factor authentication set up successfully. Please sign in to continue.
            </div>
          )}
          {setupSkipped && (
            <div className="mb-6 px-4 py-3 rounded-lg bg-blue-900/40 border border-blue-700 text-blue-300 text-sm text-center">
              Account set up. You can add two-factor authentication anytime from your account settings.
            </div>
          )}

          {/* Logo / branding */}
          <div className="mb-8 text-center">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="Logo" className="h-12 mx-auto mb-4 object-contain" />
            ) : (
              <div className="flex justify-center mb-4">
                <AllsioMark size={80} />
              </div>
            )}
            <h1
              className="text-2xl font-bold tracking-widest uppercase"
              style={{ color: 'var(--brand-accent)', fontFamily: 'var(--font-syncopate, sans-serif)' }}
            >
              {branding.platformName}
            </h1>
            <p className="mt-1 text-xs tracking-widest uppercase" style={{ color: 'var(--brand-text-subtle)', fontFamily: 'var(--font-cormorant, serif)', letterSpacing: '0.3em' }}>
              Intelligent Voice · Always Present
            </p>
            <p className="mt-2 text-sm" style={{ color: 'var(--brand-text-muted)' }}>Sign in to your account</p>
          </div>

          {step === 'credentials' ? (
            <form onSubmit={submitCredentials} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--brand-text-muted)' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 focus:outline-none transition-colors"
                  style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--brand-border)')}
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--brand-text-muted)' }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 focus:outline-none transition-colors"
                  style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--brand-border)')}
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 font-bold text-sm tracking-widest uppercase disabled:opacity-50 transition-opacity"
                style={buttonStyle}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="text-center">
                <Link
                  href="/forgot-password"
                  className="text-sm transition-colors"
                  style={{ color: 'var(--brand-text-subtle)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--brand-accent)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--brand-text-subtle)')}
                >
                  Forgot password?
                </Link>
              </div>

              {passkeyAvailable && (
                <>
                  <div className="relative my-1">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t" style={{ borderColor: 'var(--brand-border)' }} />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 text-xs" style={{ background: 'var(--brand-bg-surface)', color: 'var(--brand-text-subtle)' }}>or</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handlePasskeySignIn}
                    disabled={passkeyLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 hover:bg-[var(--brand-bg-elevated)]"
                    style={{ borderColor: 'var(--brand-border)', color: 'var(--brand-text-primary)' }}
                  >
                    {passkeyLoading ? (
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 0 1 21.75 8.25Z" />
                      </svg>
                    )}
                    {passkeyLoading ? 'Authenticating…' : 'Sign in with passkey'}
                  </button>
                </>
              )}
            </form>
          ) : (
            <form onSubmit={submitCode} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--brand-text-muted)' }}>
                  {step === 'recovery' ? 'Recovery Code' : '2FA Code'}
                </label>
                <p className="text-xs mb-3" style={{ color: 'var(--brand-text-subtle)' }}>
                  {step === 'recovery'
                    ? 'Enter one of your backup recovery codes'
                    : step === 'email-otp'
                    ? 'Enter the 6-digit code we emailed you'
                    : 'Enter the 6-digit code from your authenticator app'}
                </p>
                {step === 'recovery' ? (
                  <input
                    type="text"
                    value={code}
                    onChange={e => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 11))}
                    maxLength={11}
                    className="w-full px-4 py-2.5 focus:outline-none tracking-widest text-center text-lg transition-colors"
                    style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--brand-border)')}
                    placeholder="xxxxx-xxxxx"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                ) : (
                  <input
                    ref={totpInputRef}
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="w-full px-4 py-2.5 focus:outline-none tracking-widest text-center text-xl transition-colors"
                    style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--brand-border)')}
                    placeholder="000000"
                    autoComplete="one-time-code"
                  />
                )}
              </div>

              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 font-bold text-sm tracking-widest uppercase disabled:opacity-50 transition-opacity"
                style={buttonStyle}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              {step === 'email-otp' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={requestEmailFallback}
                    disabled={loading || resendCooldown > 0}
                    className="text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ color: 'var(--brand-text-subtle)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onMouseEnter={e => { if (resendCooldown === 0) e.currentTarget.style.color = 'var(--brand-accent)' }}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--brand-text-subtle)')}
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              )}

              {step === 'totp' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={useRecoveryCode}
                    disabled={loading}
                    className="text-sm transition-colors disabled:opacity-50"
                    style={{ color: 'var(--brand-text-subtle)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'var(--brand-accent)')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--brand-text-subtle)')}
                  >
                    Use a recovery code instead
                  </button>
                </div>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={back}
                  className="text-sm transition-colors"
                  style={{ color: 'var(--brand-text-subtle)', background: 'none', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'var(--brand-accent)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--brand-text-subtle)')}
                >
                  ← Back
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginPageInner />
    </Suspense>
  )
}
